// Libs
import ApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useMemo, useState } from "react";
// Types
import { ChartSetOne } from "../Libs/Types/ChartTypes";
// Utils
import {
  CHART_AXIS_FALLBACK,
  chartColors,
  chartHeight,
  humanize,
} from "../Libs/Utils/utils";
// UI-Components
import ChartFallBack from "../UI-Components/ChartFallback";

export default function AreaChart({
  chartData,
  dataKey,
}: {
  chartData: ChartSetOne["AreaChart"];
  dataKey: string;
}) {
  // Assign a unique color to this chart instance ONCE
  const [chartColor] = useState(() => chartColors);

  // If you have multi-series, you can generate a list of random colors ONCE per chart
  const colorList = useMemo(() => {
    if (!chartData?.yaxis) return [];
    return chartData.yaxis.map(() => chartColors);
  }, [chartData?.yaxis]);

  // Prepare y-axis configuration
  const fYData: object[] = [];
  if (chartData?.ytitle) {
    chartData.ytitle.map((value, key) => {
      let temp = {};
      if (key === 1) {
        temp = {
          ...value,
          ...{
            opposite: true,
            labels: {
              style: { fontSize: "8px" },
              formatter: (val: number) =>
                typeof val === "number" ? val.toFixed(0) : val,
            },
          },
        };
      } else {
        temp = {
          ...value,
          ...{
            labels: {
              style: { fontSize: "8px" },
              formatter: (val: number) =>
                typeof val === "number" ? val.toFixed(0) : val,
            },
          },
        };
      }
      fYData.push(temp);
      return true;
    });
  }
  // ApexCharts options
  const option: ApexOptions = {
    chart: {
      fontFamily: "Poppins",
      toolbar: { show: true },
    },
    legend: {
      show: false,
    },
    title: {
      style: { fontSize: "14px", fontWeight: "900" },
      text: chartData?.title ?? CHART_AXIS_FALLBACK.title,
      align: "left",
    },
    dataLabels: {
      style: { fontSize: "8px" },
    },
    fill: {
      colors: colorList.length > 0 ? colorList : [chartColor], // Use colorList for multi-series, or chartColor for single
      opacity: 0.1,
    },
    colors: colorList.length > 0 ? colorList : [chartColor], // Also set here for line/area color[2][3]
    xaxis: {
      categories: chartData?.xaxis?.data,
      labels: {
        style: { fontSize: "8px" },
        rotate: -90,
      },
      title: {
        text: humanize(chartData?.xaxis?.name ?? CHART_AXIS_FALLBACK.x),
      },
    },
    tooltip: {
      y: {
        title: {
          formatter: (seriesName) => humanize(seriesName),
        },
      },
    },
    yaxis: fYData,
  };

  const series: any = Array.isArray(chartData?.yaxis)
    ? chartData.yaxis
    : undefined;

  return (
    <div className="my-2 chartWrp border-1 rounded-lg w-full p-2">
      {series && option.xaxis?.categories && chartData?.ytitle ? (
        <ApexChart
          type="area"
          options={option}
          series={series}
          height={chartHeight}
          width={"100%"}
          key={dataKey}
        />
      ) : (
        <ChartFallBack />
      )}
    </div>
  );
}
